import { useEffect, useRef } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import Colors from 'src/core/utils/Colors';
import { useViewportStore } from 'src/domain/stores/useViewportStore';
import { ContentWidthContext } from 'src/presentation/components/layout/ContentWidthContext';
import { forwardWheel } from 'src/presentation/helpers/forwardWheel.web';
import type { AppViewportProps } from 'src/presentation/components/layout/AppViewportTypes';

/**
 * Boxes the app into a centred column on web, with the rest of the viewport in Topicus grey.
 *
 * Sits around the navigator, not inside it: put it inside and you get a 500px column with a tab
 * bar running the full width underneath. The measured width is published through
 * ContentWidthContext so components lay out against the column instead of the window -- on a
 * 1440px screen those differ by nearly a thousand pixels, and every module-scope
 * `Dimensions.get('window')` in this app used to get the wrong one.
 */
function AppViewport({ children }: AppViewportProps) {
	const { width } = useWindowDimensions();
	const maxContentWidth = useViewportStore((state) => state.maxContentWidth);
	const contentWidth = Math.min(width, maxContentWidth);
	const column = useRef<View | null>(null);

	// Listens on the window rather than on the grey itself: the grey is this component's own
	// background, so there is no separate element to hang it on.
	useEffect(() => {
		const onWheel = (event: WheelEvent) => {
			if (forwardWheel(event, column.current as unknown as HTMLElement | null)) {
				event.preventDefault();
			}
		};

		window.addEventListener('wheel', onWheel, { passive: false });

		return () => window.removeEventListener('wheel', onWheel);
	}, []);

	return (
		<View style={styles.viewport}>
			<View ref={column} style={[styles.column, { width: contentWidth }]}>
				<ContentWidthContext.Provider value={contentWidth}>{children}</ContentWidthContext.Provider>
			</View>
		</View>
	);
}

export default AppViewport;

const styles = StyleSheet.create({
	viewport: {
		flex: 1,
		alignItems: 'center',
		backgroundColor: Colors.greyBgAlt,
	},
	column: {
		flex: 1,
		// The column carries the app's own background; the grey around it is the viewport.
		backgroundColor: Colors.white,
		overflow: 'hidden',
	},
});
